import { useState } from 'react';
import { Eye, EyeClosed, Settings, ShareAndroid, Sparks, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { OwnerBadge } from 'components/blocks/owner-badge';
import { ResourceCard, ResourceCardLogo, ResourceCardMenu } from 'components/blocks/resource-card';
import { SharedBadge } from 'components/blocks/shared-badge';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { AddFolderToAgentDialog } from 'pages/knowledge-base/components/add-folder-to-agent-dialog';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AgentCardProviderIcons } from './agent-card-provider-icons';
import { useUpdateAgentPreferences } from 'features/chat/hooks/use-agents';
import { useToast } from 'hooks/use-toast';
import { getApiErrorDetail } from 'utils/api-error';
import { isAgentOwnerAccess, shouldShowSharedBadge } from '../utils/agent-share-access';

export type AgentListItem = AgentSummary & {
  agent_name?: string;
  created_by?: string;
};

interface AgentCardProps {
  agent: AgentListItem;
  projectKey: string;
  onSelect: (agent: AgentListItem) => void;
  onPinClick?: (agent: AgentListItem) => void;
  onConfigureClick: (agent: AgentListItem) => void;
  onShareClick: (agent: AgentListItem) => void;
  onDeleteClick: (agent: AgentListItem) => void;
  /** Hidden tab shows Unhide. Every other tab shows Hide. */
  isHiddenList?: boolean;
}

export const getAgentName = (agent: AgentListItem, fallbackName = 'Untitled Agent') =>
  agent.agent_name || agent.name || fallbackName;

export function AgentCard({
  agent,
  projectKey,
  onSelect,
  onConfigureClick,
  onShareClick,
  onDeleteClick,
  isHiddenList = false,
}: AgentCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutate: updatePreferences, isPending: isUpdatingPreferences } = useUpdateAgentPreferences();
  const [isAddAsSubagentOpen, setIsAddAsSubagentOpen] = useState(false);
  const userId = useCommonStore((state) => state.user?.id);
  const isOwner = isAgentOwnerAccess(agent, userId);
  const showSharedBadge = shouldShowSharedBadge(agent, userId);
  const name = getAgentName(agent, t('UNTITLED_AGENT') || 'Untitled Agent');
  const description = agent.description || (t('NO_DESCRIPTION') || 'No description');
  const hideLabel = isHiddenList
    ? t('UNHIDE_AGENT', { defaultValue: 'Unhide agent' })
    : t('HIDE_AGENT', { defaultValue: 'Hide agent' });
  const hideTooltip = isHiddenList
    ? t('UNHIDE_AGENT_TOOLTIP', { defaultValue: 'Show this agent in your lists again' })
    : t('HIDE_AGENT_TOOLTIP', { defaultValue: 'Hide this agent from your lists' });

  const handleHideClick = () => {
    updatePreferences(
      { agentId: agent.id, is_hidden: !isHiddenList },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: isHiddenList
              ? t('AGENT_UNHIDDEN', {
                  agentName: name,
                  defaultValue: '{{agentName}} is visible again',
                })
              : t('AGENT_HIDDEN', {
                  agentName: name,
                  defaultValue: '{{agentName}} is hidden',
                }),
            description: isHiddenList
              ? t('AGENT_UNHIDDEN_HINT', {
                  defaultValue: 'It is back in your other agent tabs.',
                })
              : t('AGENT_HIDDEN_HINT', {
                  defaultValue: 'Check it in the Hidden tab.',
                }),
          });
        },
        onError: (error) => {
          toast({
            title: t('FAILED_TO_UPDATE_AGENT', { defaultValue: 'Failed to update agent' }),
            description:
              getApiErrorDetail(error) ??
              t('PLEASE_TRY_AGAIN', { defaultValue: 'Please try again.' }),
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <>
      <ResourceCard
        title={name}
        subtitle={agent.role || undefined}
        description={description}
        icon={<ResourceCardLogo src={agent.logo_url} name={name} />}
        onSelect={() => onSelect(agent)}
        actions={
          <>
            {isOwner ? <OwnerBadge /> : null}
            <ResourceCardMenu
              items={[
                {
                  label: t('CONFIGURE') || 'Configure',
                  icon: <Settings width={24} height={24} strokeWidth={1.5} />,
                  onClick: () => onConfigureClick(agent),
                },
                isOwner && {
                  label: t('SHARE') || 'Share',
                  icon: <ShareAndroid width={24} height={24} strokeWidth={1.5} />,
                  onClick: () => onShareClick(agent),
                },
                {
                  label: t('ADD_AS_SUBAGENT') || 'Add as subagent',
                  icon: <Sparks width={24} height={24} strokeWidth={1.5} />,
                  onClick: () => setIsAddAsSubagentOpen(true),
                },
                {
                  label: hideLabel,
                  tooltip: hideTooltip,
                  icon: isHiddenList ? (
                    <Eye width={24} height={24} strokeWidth={1.5} />
                  ) : (
                    <EyeClosed width={24} height={24} strokeWidth={1.5} />
                  ),
                  disabled: isUpdatingPreferences,
                  onClick: handleHideClick,
                },
                isOwner && {
                  label: t('DELETE') || 'Delete',
                  icon: <Trash width={20} height={20} strokeWidth={1.5} />,
                  onClick: () => onDeleteClick(agent),
                  destructive: true,
                },
              ]}
            />
          </>
        }
        footer={
          <div className="flex min-h-7 items-center justify-between gap-2">
            <AgentCardProviderIcons
              tools={agent.tools}
              enableTools={agent.enable_tools}
              hasKnowledge={agent.has_knowledge}
              hasSkills={agent.has_skills}
              hasSubagents={agent.has_subagents}
              knowledgeCount={agent.folder_ids?.length ?? agent.kb_ids?.length}
              skillCount={agent.skill_ids?.length}
              subagentCount={agent.subagent_ids?.length}
            />
            <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
              {showSharedBadge ? <SharedBadge className="shrink-0" /> : null}
              <VisibilityBadge value={agent.visibility} uppercase={false} className="shrink-0" />
            </div>
          </div>
        }
      />

      <AddFolderToAgentDialog
        open={isAddAsSubagentOpen}
        onOpenChange={setIsAddAsSubagentOpen}
        projectKey={projectKey}
        resourceId={agent.id}
        resourceName={agent.name}
        queryParam="addSubagent"
        excludeAgentId={agent.id}
        title={t('ADD_AS_SUBAGENT') || 'Add as subagent'}
        description={
          t('SELECT_AGENT_TO_ADD_AS_SUBAGENT') ||
          'Select an agent to add this agent as a subagent.'
        }
      />
    </>
  );
}
